import { useEffect, useState } from "react";
import { Home, Loader2, Star, Trash2 } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { AddressInput } from "@/components/AddressInput";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const emptyPlace = { address: "", formatted_address: "", place_id: null, lat: null, lng: null };
const SUGGESTED = ["Home", "Office", "Airport"];

/** The passenger's own address book, managed from their trips page. */
export const SavedAddresses = ({ token }) => {
  const [places, setPlaces] = useState([]);
  const [label, setLabel] = useState("");
  const [place, setPlace] = useState(emptyPlace);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get(`/public/rider-auth/session/${token}/places`)
      .then(({ data }) => setPlaces(data.places))
      .catch(() => {});
  }, [token]);

  const save = async () => {
    setBusy(true); setError("");
    try {
      const { data } = await api.post(`/public/rider-auth/session/${token}/places`, { label: label.trim(), ...place });
      setPlaces((list) => [...list.filter((p) => p.label !== data.place.label), data.place]);
      setLabel(""); setPlace(emptyPlace);
    } catch (e) { setError(errMsg(e)); } finally { setBusy(false); }
  };

  const remove = async (id) => {
    const previous = places;
    setPlaces((list) => list.filter((p) => p.id !== id));
    try {
      await api.delete(`/public/rider-auth/session/${token}/places/${id}`);
    } catch (e) {
      setPlaces(previous);
      setError(errMsg(e));
    }
  };

  return (
    <div data-testid="rider-saved-addresses" className="rounded-xl border border-ink-600 bg-ink-800 p-5">
      <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-gold">
        <Star className="h-3 w-3" /> Saved addresses
      </p>
      <p className="mt-2 text-xs text-slate-500">Save the places you go most — they become one tap on the booking form.</p>

      <div className="mt-4 space-y-2">
        {places.map((p) => (
          <div key={p.id} data-testid={`rider-saved-${p.label}`} className="flex items-center gap-3 rounded-lg border border-ink-600 bg-ink-900/60 px-3 py-2.5">
            <Home className="h-4 w-4 shrink-0 text-gold" />
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm text-slate-200">{p.label}</p>
              <p className="truncate text-xs text-slate-500">{p.address}</p>
            </div>
            <button type="button" data-testid={`rider-saved-remove-${p.label}`} onClick={() => remove(p.id)}
              className="text-slate-500 transition-colors hover:text-red-400">
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        ))}
        {places.length === 0 && <p className="text-xs text-slate-600">Nothing saved yet.</p>}
      </div>

      <div className="mt-5 space-y-3 border-t border-ink-600 pt-4">
        <div className="flex flex-wrap gap-2">
          {SUGGESTED.map((s) => (
            <button type="button" key={s} data-testid={`rider-label-${s}`} onClick={() => setLabel(s)}
              className={`rounded-full border px-3 py-1 text-xs transition-colors ${
                label === s ? "border-gold/50 bg-gold/10 text-gold" : "border-ink-600 text-slate-400 hover:border-gold/30"
              }`}>
              {s}
            </button>
          ))}
        </div>
        <Input data-testid="rider-label-input" placeholder="Label (Home, Office, Mom's place…)" value={label}
          onChange={(e) => setLabel(e.target.value)}
          className="h-12 border-ink-600 bg-ink-800 text-slate-100 placeholder:text-slate-600 focus-visible:ring-gold" />
        <AddressInput testId="rider-save-address" value={place} onChange={setPlace} placeholder="Address to save…" />
        <Button data-testid="rider-save-place" disabled={busy || !label.trim() || !place.address} onClick={save}
          className="h-11 w-full bg-gold text-ink-900 hover:bg-gold-light">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save this address"}
        </Button>
        {error && <p data-testid="rider-saved-error" className="text-sm text-red-400">{error}</p>}
      </div>
    </div>
  );
};
